import React from "react";

import { Avatar, Typography, Box } from "@mui/material";

import styles from "./EducatorCard.module.scss";
import { EmployeePosition, WwccReference } from "../../../api/PartnerPortalGatewayApi";
import { useEmployeePhoto } from "../../../hooks/useEmployeePhoto";

const getWwccLabel = (state?: string): string => {
  if (!state) return "WWCC";
  const upper = state.toUpperCase();
  switch (upper) {
    case "VIC":
    case "NSW":
    case "SA":
    case "WA":
      return "WWCC";
    case "ACT":
      return "WWVP";
    case "NT":
      return "Ochre Card";
    case "QLD":
      return "Blue Card";
    default:
      return "WWCC";
  }
};

const formatWwccReference = (ref: WwccReference): string => {
  return `${getWwccLabel(ref.state)}: ${ref.reference ?? "N/A"}`;
};

interface EducatorCardProps {
  educator: EmployeePosition;
  onClick?: () => void;
  isChanged?: boolean;
}

export const EducatorCard: React.FC<EducatorCardProps> = ({ educator, onClick, isChanged = false }) => {

  const { photoUrl, isLoading } = useEmployeePhoto(educator.employeeId);

  const fullName = `${educator.firstName || ""} ${educator.lastName || ""}`.trim() || "Unknown Educator";
  const position = educator.positionName || "Unknown Position";

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <Box className={`${styles.educatorCard} ${isChanged ? styles.educatorCardChanged : ""}`}>
      <Box className={styles.cardHeader} onClick={onClick}>
        <Avatar className={styles.avatar} src={photoUrl || undefined} alt={fullName}>
          {!photoUrl && !isLoading && getInitials(fullName)}
        </Avatar>
        <Box className={styles.educatorInfo}>
          <Typography variant="body1" className={styles.name}>
            {fullName}
          </Typography>
          <Typography variant="body2" className={styles.position} color="text.secondary">
            {position}
          </Typography>
          {educator.wwccReferences && educator.wwccReferences.length > 0 && (
            <Box className={styles.wwccReferences}>
              {educator.wwccReferences.map((ref, index) => (
                <Typography key={index} variant="body2" className={styles.wwccReference} color="text.secondary">
                  {formatWwccReference(ref)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
